import type {
  AuditIssueDTO,
  AuditPlanDetailDTO,
  AuditRecordDetailDTO,
  CreateAuditIssueDTO,
  CreateAuditPlanFormDTO,
  CreateAuditRecordDTO,
  UpdateAuditIssueDTO,
  UpdateAuditPlanFormDTO,
  UpdateAuditRecordDTO,
} from "@/modules/internal-audit/domain/contracts";
import type {
  AuditTypeDTO,
  CreateAuditTypeDTO,
  UpdateAuditTypeDTO,
} from "@/modules/internal-audit/domain/contracts/type";
import type { AuditIssueListResult } from "@/modules/internal-audit/domain/repo/issue/list";
import type { AuditSummaryStats } from "@/modules/internal-audit/domain/repo/summary/get-stats";
import type { AuditTypesListResult } from "@/modules/internal-audit/domain/repo/type/list-types";
import type {
  AuditPlanListResult,
  AuditRecordListResult,
} from "@/modules/internal-audit/domain/types";
import type { OffsetPageQueryDTO } from "@/shared/contracts/base";
import { config } from "@/shared/lib/config";
import { fetcher } from "@/shared/lib/fetcher";
import { fetchLookupForInfinite, hydrateLookupItem } from "@/shared/lib/utils";

function toFormData(input: Record<string, unknown>): FormData {
  const fd = new FormData();
  Object.entries(input).forEach(([k, v]) => {
    if (v === undefined || v === null) {
      // For isActive checkbox, if undefined, don't include it (will use default on backend)
      // zfd.checkbox() expects field to be absent for false/default, or present for true
      if (k === "isActive") {
        // Don't include if undefined - backend will use default
        return;
      }
      return;
    }
    // Skip empty strings for optional fields
    if (typeof v === "string" && v === "") {
      // Only skip if it's an optional field (sopId, qcStandardId, description)
      if (
        k === "sopId" ||
        k === "qcStandardId" ||
        k === "checklistId" ||
        k === "kpiId" ||
        k === "description"
      ) {
        return;
      }
    }
    if (v instanceof File) {
      fd.append(k, v);
    } else if (v instanceof Date) {
      // Validate date is not invalid
      if (Number.isNaN(v.getTime())) {
        console.warn(`Invalid date for field ${k}, skipping`);
        return;
      }
      // Convert Date to ISO string format (YYYY-MM-DD for date inputs)
      // Use local date to avoid timezone issues
      const year = v.getFullYear();
      const month = String(v.getMonth() + 1).padStart(2, "0");
      const day = String(v.getDate()).padStart(2, "0");
      fd.append(k, `${year}-${month}-${day}`);
    } else if (Array.isArray(v) || typeof v === "object") {
      fd.append(k, JSON.stringify(v));
    } else if (typeof v === "boolean") {
      // For checkboxes, zfd.checkbox() expects "on" for true, or field absent for false
      // But we can also send "true" - let's use "on" to match HTML checkbox behavior
      if (k === "isActive") {
        fd.append(k, v ? "on" : "");
      } else {
        fd.append(k, String(v));
      }
    } else {
      fd.append(k, String(v));
    }
  });
  return fd;
}

export const auditPlanApi = {
  async list(query: OffsetPageQueryDTO): Promise<AuditPlanListResult> {
    const url = new URL(`${config.apiUrl}/internal-audit`);
    url.searchParams.set("limit", String(query.limit ?? 20));
    url.searchParams.set("offset", String(query.offset ?? 0));
    if (query.sort) url.searchParams.set("sort", JSON.stringify(query.sort));
    if (query.filters)
      url.searchParams.set("filters", JSON.stringify(query.filters));
    return fetcher.get<AuditPlanListResult>(url.toString());
  },
  async getSummaryStats(params?: {
    startDate?: string;
    endDate?: string;
    departmentId?: string;
  }): Promise<AuditSummaryStats> {
    const url = new URL(`${config.apiUrl}/internal-audit/summary/stats`);
    if (params?.startDate) url.searchParams.set("startDate", params.startDate);
    if (params?.endDate) url.searchParams.set("endDate", params.endDate);
    if (params?.departmentId)
      url.searchParams.set("departmentId", params.departmentId);
    return fetcher.get<AuditSummaryStats>(url.toString());
  },
  async get(id: string): Promise<AuditPlanDetailDTO> {
    return fetcher.get<AuditPlanDetailDTO>(
      `${config.apiUrl}/internal-audit/${id}`,
    );
  },
  async create(input: CreateAuditPlanFormDTO) {
    return fetcher.postForm<{ value: { id: string } }>(
      `${config.apiUrl}/internal-audit`,
      toFormData(input),
    );
  },
  async update(id: string, input: UpdateAuditPlanFormDTO) {
    return fetcher.putForm<{ value: { id: string } }>(
      `${config.apiUrl}/internal-audit/${id}`,
      toFormData(input),
    );
  },
  async delete(id: string) {
    return fetcher.delete<{ value: { ok: true } }>(
      `${config.apiUrl}/internal-audit/${id}`,
    );
  },
};

const auditTypesBaseUrl = `${config.apiUrl}/internal-audit/audit-types`;

export const auditTypesApi = {
  async list(query: OffsetPageQueryDTO): Promise<AuditTypesListResult> {
    const url = new URL(auditTypesBaseUrl);
    url.searchParams.set("limit", String(query.limit ?? 20));
    url.searchParams.set("offset", String(query.offset ?? 0));
    if (query.sort) url.searchParams.set("sort", JSON.stringify(query.sort));
    if (query.filters)
      url.searchParams.set("filters", JSON.stringify(query.filters));
    return fetcher.get<AuditTypesListResult>(url.toString());
  },

  async get(id: string): Promise<AuditTypeDTO> {
    return fetcher.get<AuditTypeDTO>(`${auditTypesBaseUrl}/${id}`);
  },

  async create(input: CreateAuditTypeDTO) {
    return fetcher.post<{ value: AuditTypeDTO }>(auditTypesBaseUrl, input);
  },

  async update(id: string, input: UpdateAuditTypeDTO) {
    return fetcher.patch<{ value: AuditTypeDTO }>(
      `${auditTypesBaseUrl}/${id}`,
      input,
    );
  },

  async delete(id: string) {
    return fetcher.delete<{ value: { ok: true } }>(
      `${auditTypesBaseUrl}/${id}`,
    );
  },

  async lookup(params: { search: string; pageParam: number }) {
    return fetchLookupForInfinite(`${auditTypesBaseUrl}/lookup`, params);
  },

  async hydrate(id: string) {
    return hydrateLookupItem(`${auditTypesBaseUrl}/lookup`, id);
  },
};

const auditRecordsBaseUrl = `${config.apiUrl}/internal-audit/records`;

export const auditRecordsApi = {
  async list(query: OffsetPageQueryDTO): Promise<AuditRecordListResult> {
    const url = new URL(auditRecordsBaseUrl);
    url.searchParams.set("limit", String(query.limit ?? 20));
    url.searchParams.set("offset", String(query.offset ?? 0));
    if (query.sort) url.searchParams.set("sort", JSON.stringify(query.sort));
    if (query.filters)
      url.searchParams.set("filters", JSON.stringify(query.filters));
    return fetcher.get<AuditRecordListResult>(url.toString());
  },

  async get(id: string): Promise<AuditRecordDetailDTO> {
    return fetcher.get<AuditRecordDetailDTO>(`${auditRecordsBaseUrl}/${id}`);
  },

  async create(input: CreateAuditRecordDTO) {
    return fetcher.post<{ value: AuditRecordDetailDTO }>(
      auditRecordsBaseUrl,
      input,
    );
  },

  async update(id: string, input: UpdateAuditRecordDTO) {
    return fetcher.put<{ value: AuditRecordDetailDTO }>(
      `${auditRecordsBaseUrl}/${id}`,
      input,
    );
  },

  async delete(id: string) {
    return fetcher.delete<{ value: { ok: true } }>(
      `${auditRecordsBaseUrl}/${id}`,
    );
  },
};

const auditIssuesBaseUrl = `${config.apiUrl}/internal-audit/issues`;

export const auditIssuesApi = {
  async list(query: OffsetPageQueryDTO): Promise<AuditIssueListResult> {
    const url = new URL(auditIssuesBaseUrl);
    url.searchParams.set("limit", String(query.limit ?? 20));
    url.searchParams.set("offset", String(query.offset ?? 0));
    if (query.sort) url.searchParams.set("sort", JSON.stringify(query.sort));
    if (query.filters)
      url.searchParams.set("filters", JSON.stringify(query.filters));
    return fetcher.get<AuditIssueListResult>(url.toString());
  },

  async getByRecordItem(recordItemId: string): Promise<AuditIssueDTO[]> {
    const result = await fetcher.get<{ data: AuditIssueDTO[] }>(
      `${auditIssuesBaseUrl}/by-record-item/${recordItemId}`,
    );
    return result.data;
  },

  async get(id: string): Promise<AuditIssueDTO> {
    const result = await fetcher.get<{ item: AuditIssueDTO }>(
      `${auditIssuesBaseUrl}/${id}`,
    );
    return result.item;
  },

  async create(input: CreateAuditIssueDTO) {
    return fetcher.post<{ value: { id: string } }>(auditIssuesBaseUrl, input);
  },

  async update(id: string, input: UpdateAuditIssueDTO) {
    return fetcher.put<{ value: { id: string } }>(
      `${auditIssuesBaseUrl}/${id}`,
      input,
    );
  },

  async delete(id: string) {
    return fetcher.delete<{ value: { ok: true } }>(
      `${auditIssuesBaseUrl}/${id}`,
    );
  },
};
